'use client'

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ExpenseDetail } from '@garage/shared'
import { expenseLineTotalMinor, expenseLinesSubtotalMinor } from '@garage/shared'
import { Dialog, Button, Skeleton, EmptyState, type Column, DataTable } from '@/ui'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { formatVatDate } from '@/lib/vat-date.js'

interface ExpenseDetailDialogProps {
  open: boolean
  expenseId: string | null
  onClose: () => void
  onEdit: (expense: ExpenseDetail) => void
}

export function ExpenseDetailDialog({
  open,
  expenseId,
  onClose,
  onEdit,
}: ExpenseDetailDialogProps) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  const { data: expense, isLoading, isError } = useQuery({
    queryKey: qk.expense(expenseId ?? ''),
    queryFn: () => api.expenses.get(expenseId!),
    enabled: open && !!expenseId,
  })

  if (!open) return null

  if (isLoading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={t('accounting.expenseDetails')}
        size="md"
        dismissible
      >
        <Skeleton lines={5} />
      </Dialog>
    )
  }

  if (isError || !expense) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={t('accounting.expenseDetails')}
        size="md"
        dismissible
        actions={
          <Button variant="filled" onClick={onClose}>
            {t('common.close')}
          </Button>
        }
      >
        <EmptyState
          title={t('common.failedToLoad')}
          description={t('clients.tryRefreshing')}
        />
      </Dialog>
    )
  }

  const hasLines = expense.lines.length > 0
  const subtotalMinor = expenseLinesSubtotalMinor(expense.lines)

  const columns: Column<typeof expense.lines[number]>[] = [
    {
      key: 'description',
      wrap: true,
      header: t('common.description'),
      cell: (row) => <div>{row.description}</div>,
    },
    {
      key: 'quantityMilli',
      header: t('common.quantity'),
      cell: (row) => <div className="tabular text-end">{formatters.quantity(row.quantityMilli)}</div>,
      numeric: true,
      hideOnMobile: true,
    },
    {
      key: 'unitPriceMinor',
      header: t('accounting.unitPrice'),
      cell: (row) => <div className="tabular text-end">{formatters.amount(row.unitPriceMinor)}</div>,
      numeric: true,
      hideOnMobile: true,
    },
    {
      key: 'lineTotal',
      header: t('dashboard.columns.total'),
      cell: (row) => (
        <div className="tabular text-end font-medium">
          {formatters.amount(expenseLineTotalMinor(row))}
        </div>
      ),
      numeric: true,
    },
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('accounting.expenseDetails')}
      size="md"
      dismissible
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="filled" onClick={() => onEdit(expense)}>
            {t('common.edit')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* General info block */}
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('expenseForm.invoiceNumber')}</span>
            <span className="text-body-sm text-on-surface ref">{expense.invoiceNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('calendar.date')}</span>
            <span className="text-body-sm text-on-surface">{formatVatDate(expense.date)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('accounting.supplier')}</span>
            <span className="text-body-sm text-on-surface">{expense.supplierName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('accounting.trn')}</span>
            <span className="text-body-sm text-on-surface">
              {expense.supplierTrn || <span className="text-on-surface-variant">{t('accounting.noTrn')}</span>}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-body-sm text-on-surface-variant">{t('accounting.category')}</span>
            <span className="text-body-sm text-on-surface">
              {t(`accounting.categories.${expense.category}`, expense.category)}
            </span>
          </div>
          {expense.jobcardNumber && (
            <div className="flex justify-between">
              <span className="text-body-sm text-on-surface-variant">{t('accounting.for')}</span>
              <span className="text-body-sm text-on-surface ref">{expense.jobcardNumber}</span>
            </div>
          )}
          {expense.notes && (
            <div className="flex justify-between">
              <span className="text-body-sm text-on-surface-variant">{t('clientDetail.notes')}</span>
              <span className="text-body-sm text-on-surface">{expense.notes}</span>
            </div>
          )}
        </div>

        {/* Breakdown section */}
        {hasLines ? (
          <div className="space-y-3">
            <h3 className="text-label-md text-on-surface">{t('accounting.breakdown')}</h3>
            <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
              <DataTable<typeof expense.lines[number]>
                columns={columns}
                rows={expense.lines}
                rowKey={(line) => line.id}
                stackOnMobile
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-label-md text-on-surface">{t('accounting.breakdown')}</h3>
            <div className="p-4 rounded-lg bg-surface-low border border-outline-variant text-center text-body-sm text-on-surface-variant">
              {t('accounting.notItemised')}
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="space-y-2 pt-3 border-t border-outline-variant">
          <div className="flex justify-between text-body-sm">
            <span className="text-on-surface-variant">{t('accounting.beforeVat')}</span>
            <span className="tabular text-on-surface">{formatters.amount(expense.amountBeforeVatMinor)}</span>
          </div>
          <div className="flex justify-between text-body-sm">
            <span className="text-on-surface-variant">{t('accounting.vat')}</span>
            <span className="tabular text-on-surface">{formatters.amount(expense.vatMinor)}</span>
          </div>
          <div className="flex justify-between text-body-md font-medium">
            <span className="text-on-surface">{t('dashboard.columns.total')}</span>
            <span className="tabular text-on-surface">
              {formatters.money(expense.amountBeforeVatMinor + expense.vatMinor)}
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
